import { DatenauswertungGenerisch } from '../datenauswertung/DatenauswertungGenerisch';
import { DatenauswertungJahrauswahlEinzeln } from '../datenauswertung/DatenauswertungJahrauswahlEinzeln';
import { fn2nZ } from '../../core/formatter/formatNumber';
import { IDs } from '../../helpers/IDs';
import { lazyInject } from '../../services/inversify';
import { onFormChangeUniversal } from '../dateneingabe/functions/onFormChange';
import * as React from 'react';
import { TriggerService } from '../../core/trigger/trigger_service';
import { WFormsTableWrapper } from '../../core/table/WFormsTableWrapper';

interface ZusatzToolsZertifizierungTabelle3Props extends React.ClassAttributes<any> {
    data: any;
}

interface ZusatzToolsZertifizierungTabelle3State {
    pdfString?: string;
    jahr: number;
    jahrMin: number;
    jahrMax: number;
    modus: string;
    renderButton: JSX.Element;
}

export class ZusatzToolsZertifizierungTabelle3 extends
    React.Component<ZusatzToolsZertifizierungTabelle3Props, ZusatzToolsZertifizierungTabelle3State> {

    constructor(props: any) {
        super(props);
        const startjahr: number = ((this.props.data[IDs.ENT_KONFIGURATION] || [])
            .find((item: any) => item.startjahr_datenerfassung) || {}).startjahr_datenerfassung || null;
        const jahrMax: number = new Date().getFullYear() - 1;
        const jahrMin: number = +startjahr > 1900 ? startjahr : 2010;
        this.state = {
            jahr: jahrMax,
            jahrMin,
            jahrMax,
            modus: 'bericht',
            renderButton: null
        };
        this.onFormChange = this.onFormChange.bind(this);
        this.preFilter = this.preFilter.bind(this);
    }

    @lazyInject(TriggerService) private triggerService: TriggerService;

    getValues(): any {
        const massnahmen: any[] = this.preFilter(this.props.data[IDs.ENT_ALTERNATIVES_SYSTEM_TABELLE3] || []);
        const rows: any[] = [];

        massnahmen.forEach((massnahme: any, index: number) => {
            rows.push([
                index + 1,
                (massnahme.investition_massnahme || '').trim().length ? massnahme.investition_massnahme : '-',
                massnahme.investitionssumme ? fn2nZ(massnahme.investitionssumme) : '-',
                massnahme.einsparung ? fn2nZ(massnahme.einsparung) : '-',
                massnahme.technische_nutzung ? massnahme.technische_nutzung : '-',
                massnahme.rentabilitaet_der_investition ? fn2nZ(massnahme.rentabilitaet_der_investition) : '-',
                massnahme.kapitalrueckfluss ? massnahme.kapitalrueckfluss : '-'
            ]);
        });

        return { rows, diag1: [], diag2: [] };
    }

    onFormChange(WFormData: any): void {
        onFormChangeUniversal(IDs.ENT_ALTERNATIVES_SYSTEM_TABELLE3, WFormData, this);
    }

    preFilter(data: any[]): any[] {
        return data
            .filter((item: any) =>
                (item.jahr || null) === +this.state.jahr || (!item.jahr && (item.investition_massnahme || null) === 'PV-Anlage')
            )
            .sort((a: any, b: any) =>
                +((a.investition_massnahme || '') !== 'PV-Anlage') - +((b.investition_massnahme || '') !== 'PV-Anlage')
            );
    }

    render(): JSX.Element {
        return (
            <>
                <div
                    style={{
                        backgroundColor: '#eee', maxWidth: '100%', top: 0, padding: '0 0 0 25px', position: 'sticky', zIndex: 1,
                        display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center',
                        boxSizing: 'border-box'
                    }}
                >
                    <div style={{ width: '100%', maxWidth: 1367, boxSizing: 'border-box' }}>
                        {this.renderJahresauswahl()}
                    </div>
                </div>
                {this.state.modus === 'bearbeitung' && this.state.jahr &&
                    this.renderBearbeitung()
                }
                {this.state.modus === 'bericht' &&
                    this.renderBericht()
                }
            </>
        );
    }

    renderBearbeitung(): JSX.Element {
        return (
            <WFormsTableWrapper
                entity={IDs.ENT_ALTERNATIVES_SYSTEM_TABELLE3}
                module={IDs.MOD_ETOOL}
                actions={['add', 'edit', 'delete']}
                customClass='Custom-ETA'
                formLayout='flowV2'
                addLabel='Investition/Maßnahme erfassen'
                customFormConfig={{ rentabilitaet_der_investition: { readonly: true }, kapitalrueckfluss: { readonly: true } }}
                customTdProps={(state: any, rowInfo: any, column: any) => {
                    const row: any = rowInfo.row || {};
                    return (column.id || null) === 'a' && !row.jahr && (row.investition_massnahme || null) === 'PV-Anlage'
                        ? { style: { visibility: 'hidden' } }
                        : {};
                }}
                onFormChange={this.onFormChange}
                editCondition={(row: any) => !(!row.jahr && (row.investition_massnahme || '') === 'PV-Anlage')}
                formPreset={{ jahr: +this.state.jahr }}
                preFilterFunction={this.preFilter}
                onSaveDone={() => this.triggerService.fire('reload', IDs.ENT_ALTERNATIVES_SYSTEM_TABELLE3)}
            />
        );
    }

    renderBericht(): JSX.Element {
        const numCols = 7;
        const baseWidth = 100 / numCols; // Bitte immer in Prozent angeben
        const values = this.getValues();
        const unternehmen: string = ((this.props.data[IDs.ENT_UNTERNEHMENSDATEN] || [])[0] || {}).name
            || (this.props.data.RES_MAIN_LOAD_UNTERNEHMEN_SEL || {}).bezeichnung || null;
        const config: any = {
            titel0: unternehmen,
            titel1: 'Nachweis Alternatives System',
            titel3: 'Tabelle 3 - Bewertung nach interner Verzinsung und Amortisationszeit',
            filename: 'Alternatives_System_Bewertung.pdf',
            baseWidth: baseWidth,
            header: {
                blocks: [
                    {
                        label: 'Allgemeine Angaben',
                        width: `${baseWidth * 5}%`,
                        columns: [
                            {
                                label: 'Nr', label2: ' ', customStyleLabel: { fontSize: 8 },
                                customStyleColumn: { paddingRight: 1, flexGrow: 0.5 }
                            },
                            { label: 'Investition / Maßnahme', label2: ' ', customStyleColumn: { paddingRight: 2, flexGrow: 2.4 } },
                            { label: ['Investitionssumme', 'Investitions-\nsumme'], label2: '[€]', customStyleColumn: { flexGrow: 0.7 } },
                            { label: 'Einsparung', label2: '[€/Jahr]', customStyleColumn: { flexGrow: 0.7 } },
                            { label: 'Technische Nutzung', label2: '[Jahre]', customStyleColumn: { flexGrow: 0.7 } }
                        ]
                    },
                    {
                        label: ['Interne Verzinsung', 'Interne\nVerzinsung'],
                        width: `${baseWidth}%`,
                        columns: [
                            { label: ['Rentabilität der Investition', 'Rentabilität\nder Investition'], label2: '[%]' }
                        ]
                    },
                    {
                        label: ['Statische Amortisation', 'Statische\nAmortisation'],
                        width: `${baseWidth}%`,
                        columns: [
                            { label: 'Kapitalrückfluss', label2: '[Jahre]' }
                        ]
                    }

                ]
            },
            rows: values.rows,
            row_classes: ['row_field_header'],
            rows_width: [baseWidth * 0.5, baseWidth * 2.4, baseWidth * 0.7, baseWidth * 0.7, baseWidth * 0.7],
            diagrams: []
        };

        return (
            <div style={{ margin: 10 }}>
                <div style={{ fontSize: '10pt', paddingLeft: 20 }}>
                    <b>Hinweis:</b> In diesem Bereich sind die Daten manuell einzugeben, bitte beachten Sie, dass der Datensatz zur
                    PV-Maßnahme automatisch eingetragen wird.
                </div>
                <DatenauswertungGenerisch
                    config={config}
                    orientation='portrait'
                    setRenderButton={(renderButton: JSX.Element) => this.setState({ renderButton })}
                />
            </div>
        );
    }

    renderJahresauswahl(): JSX.Element {
        return (
            <DatenauswertungJahrauswahlEinzeln
                jahrMin={this.state.jahrMin}
                jahrMax={this.state.jahrMax}
                jahr={this.state.jahr}
                externalHeaderElements={[
                    <div style={{ margin: 'auto 5px auto 5px', display: 'flex', height: '100%', flexDirection: 'row', alignItems: 'center' }}>
                        {this.renderModusauswahl()}
                    </div>,
                    ...(this.state.modus === 'bericht'
                        ? [<div style={{ margin: 'auto 0 auto auto' }}>{this.state.renderButton}</div>]
                        : [null]
                    )
                ]}
                onJahrChange={(jahr: number) => this.setState({ jahr: +jahr })}
            />
        );
    }

    renderModusauswahl(): JSX.Element {
        return (
            <>
                <label
                    style={{ cursor: 'pointer', display: 'flex' }}
                    htmlFor='modus-bericht'
                >
                    <input
                        id='modus-bericht'
                        style={{ margin: 'auto 5px' }}
                        name='modus'
                        type='radio'
                        value='bericht'
                        checked={this.state.modus === 'bericht'}
                        onChange={(event: any) => this.setState({ modus: event.target.value || undefined })}
                    />
                    Berichtsansicht
                </label>
                <label
                    style={{ marginLeft: 5, cursor: 'pointer', display: 'flex' }}
                    htmlFor='modus-bearbeitung'
                >
                    <input
                        id='modus-bearbeitung'
                        style={{ margin: 'auto 5px' }}
                        name='modus'
                        type='radio'
                        value='bearbeitung'
                        checked={this.state.modus === 'bearbeitung'}
                        onChange={(event: any) => this.setState({ modus: event.target.value || undefined })}
                    />
                    Bearbeitungsmodus (Maßnahmen eingeben)
                </label>
            </>
        );
    }
}
